import React, { createContext, useContext, useMemo } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { DashboardTheme, ThemePreset } from '../types';
import { createMuiTheme, themePresets, getThemePreset, exportTheme, importTheme } from '../utils/themeUtils';
import { useDashboard } from '../hooks/useDashboard';
import { dashboardApi } from '../utils/api';

// Theme Context Types
export interface ThemeContextType {
  currentTheme: DashboardTheme;
  themePresets: ThemePreset[];
  customThemePresets: ThemePreset[];
  adminSharedThemes: ThemePreset[];
  allThemePresets: ThemePreset[];
  isDarkMode: boolean;
  
  updateTheme: (updates: Partial<DashboardTheme>) => Promise<void>;
  applyPreset: (presetId: string) => Promise<void>;
  toggleDarkMode: () => Promise<void>;
  resetTheme: () => Promise<void>;
  exportTheme: () => string;
  importTheme: (themeJson: string) => boolean;
  saveCustomPreset: (name: string, description?: string) => Promise<ThemePreset>;
  deleteCustomPreset: (presetId: string) => Promise<void>;
  
  // Default theme management (admin/superuser only)
  setAsDefaultTheme: (description?: string) => Promise<void>;
  getDefaultTheme: () => Promise<any>;
}

// Create Theme Context
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Theme Provider Component
export const CustomThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { config, updateTheme: updateDashboardTheme, resetConfig, loadConfig } = useDashboard();
  const [adminSharedThemes, setAdminSharedThemes] = React.useState<ThemePreset[]>([]);
  
  const currentTheme = config?.theme || themePresets[0].theme;
  const isDarkMode = currentTheme.mode === 'dark';
  const customThemePresets = config?.customThemePresets || [];
  const allThemePresets = [...customThemePresets, ...adminSharedThemes, ...themePresets];

  // Load admin shared themes
  React.useEffect(() => {
    loadAdminSharedThemes();
  }, []);

  const loadAdminSharedThemes = async () => {
    try {
      const adminThemes = await dashboardApi.getAdminSharedThemes();
      setAdminSharedThemes(adminThemes);
    } catch (error) {
      console.warn('Failed to load admin shared themes:', error);
      // Don't show error to user - just means no admin themes available
    }
  };

  // Create MUI theme from dashboard theme
  const muiTheme = useMemo(() => {
    return createMuiTheme(currentTheme);
  }, [currentTheme]);

  // Update theme
  const updateTheme = async (updates: Partial<DashboardTheme>) => {
    await updateDashboardTheme(updates);
  };

  // Apply preset theme (handles predefined, custom, and admin shared presets)
  const applyPreset = async (presetId: string) => {
    // Check predefined presets first
    const preset = getThemePreset(presetId);
    if (preset) {
      await dashboardApi.applyThemePreset(preset.theme, preset.name);
      await loadConfig(); // Reload to get updated theme source tracking
      return;
    }
    
    // Check custom presets
    const customPreset = customThemePresets.find(p => p.id === presetId);
    if (customPreset) {
      await dashboardApi.applyThemePreset(customPreset.theme, customPreset.name);
      await loadConfig(); // Reload to get updated theme source tracking
      return;
    }

    // Check admin shared presets
    const adminPreset = adminSharedThemes.find(p => p.id === presetId);
    if (adminPreset) {
      await dashboardApi.applyThemePreset(adminPreset.theme, adminPreset.name);
      await loadConfig(); // Reload to get updated theme source tracking
      return;
    }
  };

  // Toggle dark mode
  const toggleDarkMode = async () => {
    const newMode = isDarkMode ? 'light' : 'dark';
    
    // Apply appropriate colors for the new mode
    const modeUpdates: Partial<DashboardTheme> = {
      mode: newMode,
    };

    if (newMode === 'dark') {
      modeUpdates.backgroundColor = '#121212';
      modeUpdates.cardBackground = '#1e1e1e';
      modeUpdates.textColor = '#ffffff';
      modeUpdates.primaryColor = '#90caf9';
      modeUpdates.secondaryColor = '#f48fb1';
    } else {
      modeUpdates.backgroundColor = '#f5f5f5';
      modeUpdates.cardBackground = '#ffffff';
      modeUpdates.textColor = '#333333';
      modeUpdates.primaryColor = '#1976d2';
      modeUpdates.secondaryColor = '#dc004e';
    }

    await updateDashboardTheme(modeUpdates);
  };

  // Reset theme to default
  const resetTheme = async () => {
    await resetConfig();
  };

  // Export current theme
  const handleExportTheme = (): string => {
    return exportTheme(currentTheme);
  };

  // Import theme from JSON
  const handleImportTheme = (themeJson: string): boolean => {
    const importedTheme = importTheme(themeJson);
    if (importedTheme) {
      updateDashboardTheme(importedTheme);
      return true;
    }
    return false;
  };

  // Save current theme as a custom preset
  const saveCustomPreset = async (name: string, description?: string): Promise<ThemePreset> => {
    const newPreset = await dashboardApi.saveCustomThemePreset(name, description);
    // Reload config to get the updated custom presets
    await loadConfig();
    return newPreset;
  };

  // Delete a custom preset
  const deleteCustomPreset = async (presetId: string): Promise<void> => {
    await dashboardApi.deleteCustomThemePreset(presetId);
    // Reload config to get the updated custom presets
    await loadConfig();
  };

  // Set current theme as default for new users (admin/superuser only)
  const setAsDefaultTheme = async (description?: string): Promise<void> => {
    await dashboardApi.setDefaultTheme(description);
  };

  // Get current default theme (admin/superuser only)
  const getDefaultTheme = async (): Promise<any> => {
    return await dashboardApi.getDefaultTheme();
  };

  const contextValue: ThemeContextType = {
    currentTheme,
    themePresets,
    customThemePresets,
    adminSharedThemes,
    allThemePresets,
    isDarkMode,
    updateTheme,
    applyPreset,
    toggleDarkMode,
    resetTheme,
    exportTheme: handleExportTheme,
    importTheme: handleImportTheme,
    saveCustomPreset,
    deleteCustomPreset,
    setAsDefaultTheme,
    getDefaultTheme,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

// Custom hook to use Theme Context
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a CustomThemeProvider');
  }
  return context;
};